// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	FLUXER_TAG_DIGIT_COUNT,
	FLUXER_TAG_LABEL,
	PREMIUM_PRODUCT_NAME,
} from '@app/features/app/config/I18nDisplayConstants';
import {LimitResolver} from '@app/features/app/utils/LimitResolverAdapter';
import {isLimitToggleEnabled} from '@app/features/app/utils/LimitUtils';
import * as PremiumModalCommands from '@app/features/premium/commands/PremiumModalCommands';
import {shouldShowPremiumFeatures} from '@app/features/premium/utils/PremiumUtils';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import {FluxerTagChangeModal} from '@app/features/user/components/modals/FluxerTagChangeModal';
import styles from '@app/features/user/components/modals/tabs/my_profile_tab/UsernameSection.module.css';
import type {User} from '@app/features/user/models/User';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {CrownIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';

interface UsernameSectionProps {
	isClaimed: boolean;
	isEmailVerified: boolean;
	user: User;
}
export const UsernameSection = observer(({isClaimed, isEmailVerified, user}: UsernameSectionProps) => {
	const {i18n} = useLingui();
	const hasCustomDiscriminator = isLimitToggleEnabled(
		{feature_custom_discriminator: LimitResolver.resolve({key: 'feature_custom_discriminator', fallback: 0})},
		'feature_custom_discriminator',
	);
	const discriminatorLabel = `#${user.discriminator}`;
	return (
		<div>
			<div className={styles.label}>
				<Trans>Username</Trans>
			</div>
			<div className={styles.actions}>
				{!isClaimed ? (
					<Tooltip text={i18n._(msg`Claim your account to change your ${FLUXER_TAG_LABEL}`)}>
						<div>
							<Button variant="primary" small disabled>
								<Trans>Change {FLUXER_TAG_LABEL}</Trans>
							</Button>
						</div>
					</Tooltip>
				) : !isEmailVerified ? (
					<Tooltip text={i18n._(msg`Verify your email to change your ${FLUXER_TAG_LABEL}`)}>
						<div>
							<Button variant="primary" small disabled>
								<Trans>Change {FLUXER_TAG_LABEL}</Trans>
							</Button>
						</div>
					</Tooltip>
				) : (
					<Button
						variant="primary"
						small
						onClick={() => ModalCommands.push(modal(() => <FluxerTagChangeModal user={user} />))}
					>
						<Trans>Change {FLUXER_TAG_LABEL}</Trans>
					</Button>
				)}
				{!hasCustomDiscriminator && shouldShowPremiumFeatures() && (
					<Tooltip
						text={i18n._(
							msg`Customize your ${FLUXER_TAG_DIGIT_COUNT}-digit tag (${discriminatorLabel}) to your liking with ${PREMIUM_PRODUCT_NAME}`,
						)}
					>
						<button
							type="button"
							onClick={() => {
								PremiumModalCommands.open();
							}}
							className={styles.premiumButton}
							aria-label={i18n._(msg`Get ${PREMIUM_PRODUCT_NAME} to customize your tag`)}
						>
							<CrownIcon weight="fill" size={18} />
						</button>
					</Tooltip>
				)}
			</div>
			<div className={styles.description}>
				<Trans>Change your username and {FLUXER_TAG_DIGIT_COUNT}-digit tag</Trans>
			</div>
		</div>
	);
});
